import { LoaderFunctionArgs } from 'react-router-dom';

const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PRODUCT_CODE_REGEX = /^[a-z]{1,16}$/i;
const CORRELATION_ID_REGEX = /^[a-z]+(?:-\d+){1,4}$/i;
const STEP_REGEX = /^\d{1,2}$/;

type Param = 'productCode' | 'step' | 'correlationId' | 'paymentId';
type ParamValidator = (value: string) => boolean;

type ParamRule =
  | {
      type: 'route';
      name: Param;
    }
  | {
      type: 'search';
      name: Param;
      aliases?: string[];
      optional?: boolean;
    };

const paramValidators: Record<Param, ParamValidator> = {
  productCode: (value: string) => PRODUCT_CODE_REGEX.test(value),
  step: (value: string) => STEP_REGEX.test(value),
  correlationId: (value: string) => CORRELATION_ID_REGEX.test(value),
  paymentId: (value: string) => GUID_REGEX.test(value),
};

const throwError = () => {
  throw new Response(null, { status: 404, statusText: 'Not Found' });
};

const createParamsValidatorLoader = (...paramRules: ParamRule[]) => {
  return ({ params, request }: LoaderFunctionArgs) => {
    for (const { name: paramName } of paramRules.filter((r) => r.type == 'route')) {
      const value = params[paramName];
      if (typeof value !== 'string' || !paramValidators[paramName](value)) {
        throwError();
      }
    }

    const searchParams = new URL(request.url).searchParams;

    for (const rule of paramRules.filter((r) => r.type == 'search')) {
      const keys = [rule.name, ...(rule.aliases ?? [])];
      const values = keys.flatMap((key) => searchParams.getAll(key));

      if ((!(rule.optional ?? false) && values.length == 0) || !values.every((value) => paramValidators[rule.name](value))) {
        throwError();
      }
    }

    return null;
  };
};

export default createParamsValidatorLoader;
